import type { i18n as I18nInstance } from "i18next";

/**
 * The procedures' periods as the server defines them once (condition 2.14), by procedure and name – `period.ec.initialReview`,
 * `period.complaint.formalExamination`, … – published with `GET /api/public/procedure-terms`. A locale string that names a period reads it
 * through interpolation (`"within {{period.ec.initialReview}} working days"`), never as fixed words; until the periods have loaded it shows a dash.
 */
export type ProcedurePeriods = Record<string, Record<string, number>>;

/** The event the translation hooks re-render on once the periods have loaded. */
export const PERIODS_LOADED = "periodsLoaded";

/** The interpolation key prefix the periods are read under. */
export const PERIOD_PREFIX = "period.";

/** Holds the published periods where every translation of the instance reads them, and re-renders the screens that name one. */
export function applyProcedurePeriods(i18n: I18nInstance, periods: ProcedurePeriods): void {
  const vars = (i18n.options.interpolation ??= {});
  const current = (vars.defaultVariables ??= {}) as Record<string, unknown>;
  const held = (current.period ??= {}) as ProcedurePeriods;
  for (const [procedure, values] of Object.entries(periods)) held[procedure] = { ...(held[procedure] ?? {}), ...values };
  i18n.emit(PERIODS_LOADED);
}

/** Reads the published periods once at start-up (`base` is the API's base URL) and applies them; a screen keeps its dashes if they cannot be read. */
export async function loadProcedurePeriods(i18n: I18nInstance, base = ""): Promise<void> {
  try {
    const res = await fetch(`${base}/api/public/procedure-terms`, { headers: { Accept: "application/json" } });
    if (!res.ok) return;
    const terms = (await res.json()) as { periods?: ProcedurePeriods | null };
    if (terms.periods) applyProcedurePeriods(i18n, terms.periods);
  } catch {
    // The periods are read again on the next start-up; nothing states a period of its own in the meantime.
  }
}
